/**
 * useNavigationBlocker - Intercepts browser navigation (back/forward) to confirm leaving
 *
 * This hook prevents accidental navigation away from a page (e.g., game room)
 * by showing a confirmation dialog when the user tries to leave via:
 * - Browser back/forward buttons
 * - Typing a new URL
 * - Closing the tab/window
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

interface UseNavigationBlockerOptions {
  when: boolean; // Whether to block navigation
  onBlock?: () => boolean | Promise<boolean>; // Called when navigation is blocked, return true to allow
  message?: string; // Message for beforeunload prompt
}

export function useNavigationBlocker({
  when,
  onBlock,
  message = "Are you sure you want to leave? Your progress may be lost.",
}: UseNavigationBlockerOptions) {
  const location = useLocation();
  const isBlocking = useRef(false);
  const lastPath = useRef(location.pathname);

  // Block browser back/forward navigation
  useEffect(() => {
    if (!when) return;

    // Push a dummy state to intercept back button
    const handlePopState = async (_event: PopStateEvent) => {
      if (isBlocking.current) return; // Prevent re-entry

      // Block navigation and show confirmation
      isBlocking.current = true;

      let shouldProceed = false;
      if (onBlock) {
        shouldProceed = await onBlock();
      } else {
        shouldProceed = window.confirm(message);
      }

      if (shouldProceed) {
        // Allow navigation
        isBlocking.current = false;
      } else {
        // Block navigation - push current location back
        window.history.pushState(null, "", lastPath.current);
        isBlocking.current = false;
      }
    };

    // Track current path
    lastPath.current = location.pathname;

    // Add extra history entry to intercept back button
    window.history.pushState(null, "", location.pathname);

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [when, onBlock, message, location.pathname]);

  // Note: beforeunload handler removed - no browser modals allowed
  // Users can close tabs/navigate away without confirmation
  // Custom modal still shown for back/forward navigation via popstate

  return {
    isBlocked: isBlocking.current,
  };
}
